import { useEffect, useMemo, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import {
  FaExpand,
  FaFacebookF,
  FaHouse,
  FaLink,
  FaShareNodes,
  FaTelegram,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Lightbox from "yet-another-react-lightbox";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/styles.css";

import { getImageUrl } from "../../utils/imageUrl";

function normalizeImage(item, fallbackAlt) {
  if (!item) return null;

  if (typeof item === "string") {
    return { url: getImageUrl(item), alt: fallbackAlt };
  }

  if (typeof item === "object") {
    const source = item.display_image_url || item.watermarked_image_url || item.url || item.image_url || item.path || item.src || item.original_image_url;
    if (!source) return null;

    return {
      url: getImageUrl(source),
      alt: item.alt || item.alt_text || fallbackAlt,
    };
  }

  return null;
}

const buildShareText = (title) => `Lihat properti ini: ${title}`;

const DetailGallery = ({ images = [], title = "Property" }) => {
  const normalizedImages = useMemo(() => {
    const fallbackAlt = title || "Property image";
    const mapped = images.map((item) => normalizeImage(item, fallbackAlt)).filter(Boolean);

    return mapped.length
      ? mapped
      : [{ url: "/images/placeholder-property.jpg", alt: fallbackAlt }];
  }, [images, title]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setActiveIndex(0);
  }, [title]);

  const slides = useMemo(
    () => normalizedImages.map((img) => ({ src: img.url, alt: img.alt })),
    [normalizedImages],
  );

  const openLightbox = (index = activeIndex) => {
    setActiveIndex(index);
    setLightboxOpen(true);
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = buildShareText(title);

  const handleNativeShare = async () => {
    try {
      if (navigator?.share) {
        await navigator.share({ title, text: shareText, url: shareUrl });
        return;
      }

      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  return (
    <div className="detail-gallery">
      <div className="detail-gallery-main">
        <div className="detail-gallery-actions">
          <Dropdown align="end">
            <Dropdown.Toggle
              as={Button}
              variant="light"
              className="detail-gallery-icon-btn detail-gallery-share-toggle"
              aria-label="Bagikan properti"
            >
              <FaShareNodes />
            </Dropdown.Toggle>

            <Dropdown.Menu className="detail-share-menu">
              <Dropdown.Header>Bagikan</Dropdown.Header>
              <Dropdown.Item onClick={handleNativeShare}>
                <FaShareNodes /> {copied ? "Link disalin" : "Share"}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => openShare(`https://wa.me/?text=${encodedText}%20${encodedUrl}`)}>
                <FaWhatsapp /> WhatsApp
              </Dropdown.Item>
              <Dropdown.Item onClick={() => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)}>
                <FaFacebookF /> Facebook
              </Dropdown.Item>
              <Dropdown.Item onClick={() => openShare(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`)}>
                <FaTelegram /> Telegram
              </Dropdown.Item>
              <Dropdown.Item onClick={() => openShare(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`)}>
                <FaXTwitter /> X
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleNativeShare}>
                <FaLink /> Salin link
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button
            variant="light"
            className="detail-gallery-icon-btn"
            onClick={() => openLightbox(activeIndex)}
            aria-label="Lihat gambar penuh"
          >
            <FaExpand />
          </Button>
        </div>

        {normalizedImages.length > 0 ? (
          <Swiper
            modules={[Autoplay, EffectFade, Pagination]}
            slidesPerView={1}
            loop={normalizedImages.length > 1}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            autoplay={{
              delay: 3500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{ clickable: true }}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            className="detail-gallery-swiper"
          >
            {normalizedImages.map((img, index) => (
              <SwiperSlide key={`${img.url}-${index}`}>
                <button
                  type="button"
                  className="detail-gallery-image-button"
                  onClick={() => openLightbox(index)}
                  aria-label={`Lihat gambar ${index + 1}`}
                >
                  <img
                    src={img.url}
                    alt={img.alt || title}
                    className="detail-gallery-image"
                    loading={index === 0 ? "eager" : "lazy"}
                    decoding="async"
                  />
                  <span className="detail-gallery-zoom">
                    <FaExpand />
                  </span>
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="detail-gallery-placeholder">
            <FaHouse size={42} />
            <div>Tidak ada gambar tersedia</div>
          </div>
        )}
      </div>

      {normalizedImages.length > 1 && (
        <div className="detail-gallery-thumbs">
          {normalizedImages.slice(0, 3).map((img, index) => (
            <button
              key={`${img.url}-${index}`}
              type="button"
              className={`detail-gallery-thumb ${index === activeIndex ? "active" : ""}`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Pilih gambar ${index + 1}`}
            >
              <img src={img.url} alt={img.alt || title} />
            </button>
          ))}

          <button
            type="button"
            className="detail-gallery-thumb detail-gallery-thumb-more"
            onClick={() => openLightbox(0)}
            aria-label="Lihat semua gambar"
          >
            <img src={normalizedImages[0]?.url} alt={title} />
            <span>Lihat Semua</span>
          </button>
        </div>
      )}

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={activeIndex}
        slides={slides}
        plugins={[Zoom, Fullscreen, Thumbnails]}
        carousel={{ finite: false }}
        thumbnails={{
          position: "bottom",
          width: 104,
          height: 72,
          border: 2,
          borderRadius: 12,
          padding: 4,
          gap: 8,
        }}
        zoom={{ maxZoomPixelRatio: 3 }}
      />
    </div>
  );
};

export default DetailGallery;
